// The four ports the Obsidian adapter implements and tests fake
// (plugin spec §Ports). `packages/core` has ZERO Obsidian imports; it speaks
// only these interfaces. Method signatures track the spec verbatim; where the
// spec was terse (WriteResult, SyncSocket, HttpClient, KVStore) the shapes
// chosen here are documented so the adapter and Layer-2 tests agree.

/**
 * The WebSocket subprotocol the server advertises and every client MUST
 * request (protocol spec §Versioning; mirrors `crates/protocol`'s
 * `WS_SUBPROTOCOL`). Kept here in the shared net contract — not per-adapter —
 * so the viewer and the Obsidian plugin request the exact same value and the
 * Provider enforces acceptance in one place. A `Sec-WebSocket-Protocol` value
 * is an RFC 7230 token, so the version is joined with `.`.
 */
export const WS_SUBPROTOCOL = "cairn-proto.1";

/** A non-ignored file as seen by the vault (path policy pre-applied). */
export interface FileStat {
  path: string;
  size: number;
  mtime: number;
  isText: boolean;
}

/**
 * Result of a bridge-initiated write. `conflict` means the file changed on
 * disk since `ifUnchangedSince` (plugin spec: bridge re-diffs and rewrites).
 */
export type WriteResult =
  | { status: "written"; mtime: number }
  | { status: "conflict" };

/** A filesystem event surfaced by the vault (Obsidian-shaped). */
export type VaultFsEventType = "create" | "modify" | "rename" | "delete";
export interface VaultFsEvent {
  type: VaultFsEventType;
  path: string;
  /** Present only for `rename`. */
  oldPath?: string;
}

export interface VaultPort {
  /** Snapshot of all non-ignored files (path policy pre-applied). */
  list(): Promise<FileStat[]>;
  readText(path: string): Promise<string>;
  readBinary(path: string): Promise<ArrayBuffer>;
  writeText(
    path: string,
    text: string,
    opts: { ifUnchangedSince?: number },
  ): Promise<WriteResult>;
  writeBinary(path: string, data: ArrayBuffer): Promise<WriteResult>;
  rename(from: string, to: string): Promise<void>;
  /** Remove to OS trash where supported. */
  remove(path: string): Promise<void>;
  mkdirp(dir: string): Promise<void>;
  events(): AsyncIterable<VaultFsEvent>;
}

/** Last-synced snapshot of a file (the bridge's shadow table row). */
export interface ShadowEntry {
  path: string;
  fileId: string;
  contentHash: string;
  lastSyncedMtime: number;
  /**
   * Last-synced text of a markdown file (the merge base). Required for the
   * three-way "both changed" merge — the reconcile diffs disk against this,
   * not against the current CRDT text, so remote edits survive (plugin spec
   * §Startup reconcile). Absent for attachments (hash is enough).
   */
  baseText?: string;
  /**
   * Set on an attachment whose bytes are not yet confirmed uploaded. The meta
   * entry + shadow are committed before the upload lands (meta-first ordering,
   * decisions/40), so this flag is the durable record that the transfer is
   * still owed: it survives restart, `start()` re-queues the upload from disk
   * bytes, and it is cleared only on a confirmed server-side commit. Absent /
   * false = the bytes are durable.
   */
  uploadPending?: boolean;
}

/** Small string KV (server_url, session, vault binding, …). */
export interface KVStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
}

/** Single IndexedDB database for all docs (plugin spec §PersistencePort). */
export interface PersistencePort {
  loadDoc(
    docId: string,
  ): Promise<{ updates: Uint8Array[]; epoch: number } | null>;
  appendUpdate(docId: string, update: Uint8Array): Promise<void>;
  compactDoc(docId: string, state: Uint8Array, epoch: number): Promise<void>;
  dropDoc(docId: string): Promise<void>;
  getShadow(): Promise<ShadowEntry[]>;
  putShadow(e: ShadowEntry): Promise<void>;
  deleteShadow(path: string): Promise<void>;
  kv: KVStore;
}

/** Reason a socket closed, mirrored from the protocol `CloseReason`. */
export interface SocketCloseInfo {
  code: number;
  reason: string;
}

/**
 * A single multiplexed WebSocket to the data plane. Frames are protobuf
 * `ClientMessage`/`ServerMessage` envelopes (opaque `Uint8Array` here — the
 * Provider does the encode/decode).
 */
export interface SyncSocket {
  send(frame: Uint8Array): void;
  onOpen(cb: () => void): void;
  onMessage(cb: (frame: Uint8Array) => void): void;
  onClose(cb: (info: SocketCloseInfo) => void): void;
  close(code?: number, reason?: string): void;
  /**
   * The WebSocket subprotocol the server selected during the handshake, or the
   * empty string if it selected none. The Provider verifies this equals
   * {@link WS_SUBPROTOCOL} on open and fails loudly otherwise (protocol spec
   * §Versioning) — so the check lives in one place for every adapter, and no
   * transport can silently downgrade.
   */
  protocol(): string;
}

/** Minimal REST client for `/api/v1` (login, token mint, attachments, …). */
export interface HttpClient {
  request<T>(opts: {
    method: string;
    path: string;
    body?: unknown;
    headers?: Record<string, string>;
  }): Promise<{ status: number; body: T }>;
  /**
   * PUT raw bytes to an absolute URL (a presigned S3/MinIO upload). The
   * transport sets `Content-Length` from the body length, which the presign
   * signed exactly (control-plane spec §attachments) — no extra headers.
   */
  putBinary(url: string, data: ArrayBuffer): Promise<{ status: number }>;
  /** GET raw bytes from an absolute URL (a presigned S3/MinIO download). */
  getBinary(url: string): Promise<{ status: number; body: ArrayBuffer }>;
}

export interface NetPort {
  /** Open a sync WebSocket. The URL carries no token (auth is first-frame). */
  connect(wsUrl: string): SyncSocket;
  http: HttpClient;
}

export interface ClockPort {
  now(): number;
  timer(ms: number): Promise<void>;
}
